import type { HttpService } from '@bytel/leviator';
import { getTrackerId } from '@bytel/logger-nextjs/server';

import { getAppConfig } from '@services/config';

import type { BytelAxiosRequestConfig, Callback } from '@app-types/axios';
import type { UserType } from '@app-types/user';

import { getClientCookies } from '@helpers/cookies-client';
import { isServer } from '@helpers/env';

import { API_X_SOURCE, SAPIC_X_VERSION } from '@constants/api';

import type { AxiosInstance, AxiosResponse } from 'axios';
import createHash from 'create-hash';
import { unstable_cache } from 'next/cache';
import { v4 as uuid } from 'uuid';

import type { HttpConfig } from './http';

const appConfig = getAppConfig();

export class AxiosService implements HttpService<HttpConfig> {
    private readonly isApi: boolean;
    private readonly isCms: boolean;
    private readonly httpInstance: AxiosInstance;
    private readonly cachedHttpInstance: AxiosInstance;
    private readonly headers: Record<string, string> = {};

    constructor(axiosInstance: AxiosInstance, isApi: boolean = false, isCms: boolean = false) {
        this.httpInstance = axiosInstance;
        this.cachedHttpInstance = axiosInstance.create({
            adapter: 'fetch',
        });
        this.isApi = isApi;
        this.isCms = isCms;
    }

    public async get<Response, Body = object | undefined>(
        url: string,
        data?: Body,
        httpConfig: HttpConfig = {},
    ): Promise<Response> {
        const config = await this.initConfig(httpConfig);
        const cacheKey = this.generateCacheKey(url, data, httpConfig.user);

        config.params = data;

        const instance = httpConfig.ttl !== undefined ? this.cachedHttpInstance : this.httpInstance;

        if (httpConfig.ttl !== undefined) {
            config.fetchOptions = { cache: httpConfig.ttl === 0 ? 'no-cache' : 'force-cache' };
        }

        return await this.launchRequest(
            async () => {
                return await instance.get(url, config).then((response) => this.processResponse<Response>(response));
            },
            cacheKey,
            config,
        )().catch(async (error) => {
            if ((httpConfig.ttl ?? 0) === 0 || typeof window === 'undefined') {
                throw error;
            }

            config.fetchOptions = { cache: 'no-cache' };

            return await instance.get(url, config).then((response) => this.processResponse<Response>(response));
        });
    }

    public async post<Response, Body = object>(
        url: string,
        data?: Body,
        httpConfig: HttpConfig = {},
    ): Promise<Response> {
        const config = await this.initConfig(httpConfig);
        const cacheKey = this.generateCacheKey(url, data, httpConfig.user);

        return await this.launchRequest(
            () => {
                return this.httpInstance
                    .post<Response, AxiosResponse<Response>, Body>(url, data, config)
                    .then((response) => this.processResponse<Response>(response));
            },
            cacheKey,
            config,
        )();
    }

    public async put<Response, Body = object>(
        url: string,
        data?: Body,
        httpConfig: HttpConfig = {},
    ): Promise<Response> {
        const config = await this.initConfig(httpConfig);
        const cacheKey = this.generateCacheKey(url, data, httpConfig.user);

        return await this.launchRequest(
            () => {
                return this.httpInstance
                    .put<Response, AxiosResponse<Response>, Body>(url, data, config)
                    .then((response) => this.processResponse<Response>(response));
            },
            cacheKey,
            config,
        )();
    }

    public async delete<T>(url: string, httpConfig: HttpConfig = {}) {
        const config = await this.initConfig(httpConfig);

        return await this.httpInstance.delete<T>(url, config).then((response) => this.processResponse<T>(response));
    }

    private launchRequest<T extends Callback>(cb: T, key: string, config: BytelAxiosRequestConfig): T {
        if (!config.next?.revalidate || typeof window !== 'undefined') {
            return cb;
        }

        return unstable_cache(cb, [key], {
            tags: config?.next?.tags,
            revalidate: config?.next?.revalidate,
        });
    }

    protected processResponse<T>(response: AxiosResponse): T {
        return response.data;
    }

    protected async initConfig(httpConfig: HttpConfig = {}): Promise<BytelAxiosRequestConfig> {
        let headersObject = httpConfig.headers ?? {};

        const config: BytelAxiosRequestConfig = {
            signal: httpConfig?.controller ? httpConfig.controller.signal : undefined,
            timeout: appConfig.axios.timeout,
        };

        if (httpConfig.user) {
            config.user = {
                idPersonne: httpConfig.user.sub,
                login: httpConfig.user.login,
                userType: httpConfig.user.user_type,
            };
        }

        if (httpConfig.ttl !== undefined) {
            config.next = {
                ...config.next,
                revalidate: httpConfig.ttl * 60,
            };
        }

        if (httpConfig?.tags && httpConfig.tags.length > 0) {
            config.next = {
                ...config.next,
                tags: httpConfig.tags,
            };
        }

        if (this.isCms) {
            config.next = {
                ...config.next,
                mute: true,
            };
        } else {
            headersObject['content-type'] ??= 'application/json';
        }

        headersObject = await this.setHeadersApi(httpConfig, headersObject);
        headersObject = this.cleanHeaders(headersObject);

        return { ...config, headers: headersObject };
    }

    private async setHeadersApi(
        httpConfig: HttpConfig,
        baseHeaders: Record<string, string>,
    ): Promise<Record<string, string>> {
        const headers = { ...baseHeaders };

        headers['x-source'] = API_X_SOURCE;
        headers['x-request-id'] = uuid();

        const trackerId = isServer() ? await getTrackerId() : getClientCookies('bytel_logger_trackerId');
        if (trackerId) {
            headers.trackerId = trackerId;
        }

        if (this.isApi) {
            headers['x-version'] ??= SAPIC_X_VERSION.V5;

            headers['accept-encoding'] = 'gzip';

            const trueClientIp = this.headers['true-client-ip'];

            if (trueClientIp) {
                headers['x-real-ip'] = trueClientIp;
                headers['x-bytel-ip'] = trueClientIp;
                headers['x-last-ip'] = trueClientIp;
            }

            if (appConfig.bench && !headers['x-banc']) {
                headers['x-banc'] = appConfig.bench;
            }

            if (httpConfig.user) {
                headers.authorization = `Bearer ${httpConfig.user.accessToken}`;
            }
        }

        return headers;
    }

    private cleanHeaders(headers: Record<string, string>): Record<string, string> {
        delete headers.host;
        delete headers.accept;
        delete headers['accept-language'];
        delete headers.connection;
        delete headers['content-length'];
        delete headers.origin;
        return headers;
    }

    private generateCacheKey<Body>(url: string, data?: Body, user?: UserType): string {
        const keyParts = [url];
        if (data && Object.keys(data).length > 0) {
            keyParts.push(JSON.stringify(data));
        }
        if (user?.login) {
            keyParts.push(JSON.stringify(user.login));
        }
        const rawKey = keyParts.join('-');

        return createHash('sha256').update(rawKey).digest('hex');
    }
}
